import CaptchaTokenManager from "./CaptchaTokenManager";

export default class DNTcaptchaInitializer {
    private dntCaptchaLayoutJq: JQuery;

    private containerChildrenObserver: MutationObserver;

    private readonly refreshFunctionName = "onCaptchaRefreshButtonDataAjaxFailure";

    public prepareContainer(jq: JQueryStatic, container: JQuery, captchaTokenManager: CaptchaTokenManager): void {
        if (!this.dntCaptchaLayoutJq) {
            this.dntCaptchaLayoutJq = jq("#dnt-captcha-elements-selector");
        }

        const dntBlockInContainer = container.find(".captcha-elements-container-selector");

        if (!dntBlockInContainer.length) {
            return; // container has no prepared block for captcha, skip it
        }

        // top parent that may include additional layout and sets visibility
        const dntBlockTopParentInContainer = container.find(".dnt-captcha-container-top-parent-selector");

        this.initErrorHandlingFunction(jq, container, captchaTokenManager);

        if (!captchaTokenManager.getTokenParam()) {
            this.refreshCaptchaChallenge();
            this.startChildMutationObserver(dntBlockInContainer);
            dntBlockInContainer.append(this.dntCaptchaLayoutJq.contents().detach());
            if (dntBlockTopParentInContainer.length) {
                dntBlockTopParentInContainer.removeClass("d-none");
            } else {
                dntBlockInContainer.removeClass("d-none");
            }
        }

        // Fix validation messages localization on dynamically added containers
        container.removeData("validator");
        container.removeData("unobtrusiveValidation");
        jq.validator.unobtrusive.parse(container);
    }

    public cleanupContainer(container: JQuery): void {
        this.deinitErrorHandlingFunction();
        this.containerChildrenObserver?.disconnect();
        const dntBlockInContainer = container.find(".captcha-elements-container-selector");
        // top parent that may include additional layout and sets visibility
        const dntBlockTopParentInContainer = container.find(".dnt-captcha-container-top-parent-selector");
        this.dntCaptchaLayoutJq.append(dntBlockInContainer.contents().detach());
        if (dntBlockTopParentInContainer.length) {
            dntBlockTopParentInContainer.addClass("d-none");
        } else {
            dntBlockInContainer.addClass("d-none");
        }
    }

    public init(jq: JQueryStatic, container: JQuery, callback: (inputJq: JQuery<HTMLInputElement>) => any): void {
        const dntBlockInContainer = container.find(".captcha-elements-container-selector");
        const input = dntBlockInContainer.find("input") as JQuery<HTMLInputElement>;
        const inputsCopy = input.clone();
        inputsCopy.attr("type", "hidden");
        this.cleanupContainer(container);
        callback(inputsCopy);
    }

    private initErrorHandlingFunction(jq: JQueryStatic, container: JQuery, captchaTokenManager: CaptchaTokenManager): void {
        (window as any)[this.refreshFunctionName] = (xhr: XMLHttpRequest) => this.handleRequestError(xhr, jq, container, captchaTokenManager);
    }

    private deinitErrorHandlingFunction(): void {
        (window as any)[this.refreshFunctionName] = undefined;
    }

    private handleRequestError(xhr: XMLHttpRequest, jq: JQueryStatic, container: JQuery, captchaTokenManager: CaptchaTokenManager): void {
        const refreshBtnJq = container.find(".refresh-button-selector");
        refreshBtnJq.attr("data-ajax", "false");
        const captchaExpiredMsgJq = jq(".captcha-expired-msg-selector");
        captchaExpiredMsgJq.removeClass("d-none").addClass("d-block");

        refreshBtnJq.on("click", () => {
            const url = captchaExpiredMsgJq.attr("data-partial-view-url");
            const ajax = jq.get(url);
            ajax.done((response: string) => {
                refreshBtnJq.off("click");
                this.cleanupContainer(container);
                this.dntCaptchaLayoutJq = jq(response);
                this.prepareContainer(jq, container, captchaTokenManager);
            });
            ajax.fail(() => {
                const reloadFailedErrorMsgJq = jq(".captcha-reload-failed-msg-selector");
                reloadFailedErrorMsgJq.removeClass("d-none").addClass("d-block");
            });
            ajax.always(() => {
                captchaExpiredMsgJq.removeClass("d-block").addClass("d-none");
            });
        });
    }

    private refreshCaptchaChallenge(): void {
        const refreshLinkJq = this.dntCaptchaLayoutJq.find(".refresh-button-selector") as JQuery<HTMLAnchorElement>;
        refreshLinkJq.trigger("click");
    }

    private startChildMutationObserver(dntBlockInContainer: JQuery): void {
        const targetNode = dntBlockInContainer.get(0);
        const config = {attributes: false, childList: true, subtree: false};
        this.containerChildrenObserver = new MutationObserver(this.onCaptchaContainerChildrenChange.bind(this));
        this.containerChildrenObserver.observe(targetNode, config);
    }

    private onCaptchaContainerChildrenChange(mutations: MutationRecord[], observer: MutationObserver): void {
        for (const mutation of mutations) {
            if (mutation.type === "childList") {
                const target = mutation.target;
                if (!(target instanceof HTMLElement)) {
                    return;
                }
                const refreshButton = target.querySelector(".refresh-button-selector");
                if (refreshButton) {
                    refreshButton.setAttribute("tabindex", "9999");
                    // to allow handling errored AJAX call while avoiding having to modify tag helper on server,
                    // add an unobtrusive ajax attribute in JS
                    refreshButton.setAttribute("data-ajax-failure", this.refreshFunctionName);
                }
            }
        }
    }
}
